import React, { Suspense, lazy, useCallback, useEffect } from "react";

import { connect } from "react-redux";
import { useLocation } from "react-router-dom";
import { bindActionCreators } from "redux";

import { useAuth0 } from "@auth0/auth0-react";
import { CssBaseline } from "@material-ui/core";
import * as Sentry from "@sentry/react";

import { audiences } from "./Routes";
import { useUser } from "./contexts/user-context";
import Notifications from "./layout/Notifications";
import SplashScreen from "./layout/SplashScreen";
import Pages from "./pages";
import { appActions, userActions } from "./store/actions";
import actionTypes from "./store/user/actionTypes";

const Layout = lazy(() => import("./layout/Layout"));

function App({ user: userFromRedux, prp, appActions, userActions }: any) {
  const { pathname } = useLocation();
  const {
    state: { user, isAuthenticated: isAuthenticatedContext },
    dispatch,
  } = useUser();

  const {
    user: userFromAuth0,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
  } = useAuth0();

  const { isAuthenticated: isAuthenticatedRedux, accessToken } = userFromRedux;

  const requestAccessToken = useCallback(
    async (options: any) => {
      try {
        return await getAccessTokenSilently(options);
      } catch (e) {
        try {
          return await getAccessTokenWithPopup(options);
        } catch (e: unknown) {
          Sentry.captureException(e, {
            tags: {
              action: actionTypes.AUTHENTICATE,
            },
          });
          if (e instanceof Error) {
            appActions.storeNotification("error", e.message);
          }
        }
      }
    },
    [getAccessTokenSilently, getAccessTokenWithPopup, appActions]
  );

  const updateToken = useCallback(
    async (scope: string) => {
      const accessToken = await requestAccessToken({
        audience: audiences.prp,
        scope,
      });

      userActions.updateAccessToken(accessToken);
    },
    [userActions, requestAccessToken]
  );

  useEffect(() => {
    if (isAuthenticatedContext && user && audiences.prp) {
      if (prp.error?.status === 401) {
        const scope = user.roles[audiences.prp].join(" ");
        updateToken(scope);
      }
    }
  }, [prp.error, user, updateToken, isAuthenticatedContext]);

  useEffect(() => {
    const login = async () => {
      if (!isAuthenticated) {
        await loginWithRedirect({
          appState: { targetUrl: pathname },
          redirectUri: `${window.location.origin}#${pathname}`,
        });
      }
    };

    if (!isLoading && !isAuthenticatedContext) {
      if (!isAuthenticated) {
        login();
      } else {
        if (userFromAuth0) {
          const app_metadata =
            userFromAuth0[process.env.REACT_APP_AUTH0_APP_METADATA_NAMESPACE!];
          const roles =
            userFromAuth0[process.env.REACT_APP_AUTH0_ROLES_NAMESPACE!];

          const updatedUser: any = {
            ...userFromAuth0,
            app_metadata,
            roles,
          };

          delete updatedUser[
            process.env.REACT_APP_AUTH0_APP_METADATA_NAMESPACE!
          ];
          delete updatedUser[process.env.REACT_APP_AUTH0_ROLES_NAMESPACE!];

          const id = app_metadata.employee_number || userFromAuth0.sub;
          Sentry.setUser({ id });

          dispatch({
            type: "finish update",
            updatedUser,
          });
          userActions.authenticateSuccess(updatedUser);

          const scope = updatedUser.roles[audiences.prp]?.join(" ");
          updateToken(scope);
        }
      }
    }
  }, [
    userFromAuth0,
    isLoading,
    isAuthenticated,
    isAuthenticatedContext,
    userActions,
    dispatch,
    loginWithRedirect,
    requestAccessToken,
    updateToken,
    pathname,
  ]);

  if ((!isAuthenticated && !isAuthenticatedRedux) || !accessToken) {
    return <SplashScreen />;
  }
  return (
    <React.Fragment>
      <CssBaseline />
      <Notifications />
      <Suspense fallback={<SplashScreen />}>
        <Layout>
          <Pages />
        </Layout>
      </Suspense>
    </React.Fragment>
  );
}

const mapStateToProps = (state: any) => ({
  user: state.user,
  prp: state.prp,
});

const mapDispatchToProps = (dispatch: any) => ({
  userActions: bindActionCreators(userActions, dispatch),
  appActions: bindActionCreators(appActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(App);
